import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuthTableMonitor = ({ addLog }) => {
  const [authData, setAuthData] = useState({
    usersCount: 0,
    identitiesCount: 0,
    mismatchCount: 0,
    recentUsers: [],
    recentIdentities: []
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'identities'

  // Fetch auth table data
  const fetchAuthData = async () => {
    setLoading(true);
    addLog('info', 'Fetching auth table data...');
    
    try {
      // Get users count (via RPC or direct query if permissions allow)
      const { count: usersCount, error: usersError } = await supabase?.from('user_profiles')?.select('*', { count: 'exact', head: true });
      
      // Get recent user profiles
      const { data: recentUsers, error: recentUsersError } = await supabase?.from('user_profiles')?.select('id, full_name, user_type, created_at')?.order('created_at', { ascending: false })?.limit(10);
      
      // Get current session info
      const { data: sessionData } = await supabase?.auth?.getSession();
      
      setAuthData({
        usersCount: usersCount || 0,
        identitiesCount: 0, // Would need RPC function to access auth.identities
        mismatchCount: 0,
        recentUsers: recentUsers || [],
        currentSession: sessionData?.session || null
      });
      
      addLog('success', 'Auth table data fetched', { 
        usersCount, 
        recentUsersCount: recentUsers?.length 
      });
    } catch (error) {
      addLog('error', 'Failed to fetch auth table data', { error: error?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuthData();
  }, []);

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Database" size={20} />
          Auth Table Monitor
        </h2>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchAuthData}
          disabled={loading}
          className="flex items-center gap-2"
        >
          {loading ? (
            <Icon name="Loader2" size={16} className="animate-spin" />
          ) : (
            <Icon name="RefreshCw" size={16} />
          )}
          Refresh
        </Button>
      </div>
      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
          <div className="text-2xl font-bold text-blue-500">{authData?.usersCount}</div>
          <div className="text-xs text-muted-foreground">User Profiles</div>
        </div>
        <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
          <div className="text-2xl font-bold text-green-500">{authData?.identitiesCount}</div>
          <div className="text-xs text-muted-foreground">Identities</div>
        </div>
        <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
          <div className="text-2xl font-bold text-yellow-500">{authData?.mismatchCount}</div>
          <div className="text-xs text-muted-foreground">Mismatches</div>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-border">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'users' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          Recent Users
        </button>
        <button
          onClick={() => setActiveTab('session')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'session' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
          }`}
        >
          Current Session
        </button>
      </div>
      {/* Content */}
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {activeTab === 'users' && (
          authData?.recentUsers?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Users" size={32} className="mx-auto mb-2 opacity-50" />
              <p>No users found</p>
            </div>
          ) : (
            authData?.recentUsers?.map((user) => (
              <div
                key={user?.id}
                className="p-3 rounded-lg border border-border bg-background/50 text-sm"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-foreground">{user?.full_name || 'Unnamed User'}</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(user.created_at)?.toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <code className="bg-muted px-2 py-0.5 rounded font-mono">{user?.id}</code>
                  <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                    {user?.user_type}
                  </span>
                </div>
              </div>
            ))
          )
        )}

        {activeTab === 'session' && (
          authData?.currentSession ? (
            <div className="space-y-3">
              <div className="p-3 rounded-lg border border-border bg-background/50">
                <div className="text-sm space-y-2">
                  <div>
                    <span className="text-muted-foreground">User ID:</span>
                    <code className="ml-2 text-xs bg-muted px-2 py-0.5 rounded font-mono">
                      {authData?.currentSession?.user?.id}
                    </code>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Email:</span>
                    <code className="ml-2 text-xs bg-muted px-2 py-0.5 rounded">
                      {authData?.currentSession?.user?.email}
                    </code>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Expires At:</span>
                    <span className="ml-2 text-xs">
                      {new Date(authData.currentSession.expires_at * 1000)?.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Token Valid:</span>
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                      authData?.currentSession?.expires_at * 1000 > Date.now()
                        ? 'bg-green-500/10 text-green-500' :'bg-red-500/10 text-red-500'
                    }`}>
                      {authData?.currentSession?.expires_at * 1000 > Date.now() ? 'Valid' : 'Expired'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="UserX" size={32} className="mx-auto mb-2 opacity-50" />
              <p>No active session</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default AuthTableMonitor;